/**
 * Error Monitoring Configuration
 * Central configuration for error tracking and monitoring
 */

export interface ErrorMonitoringConfig {
  /** Whether error monitoring is enabled */
  enabled: boolean;
  /** Error monitoring provider */
  provider: "sentry" | "none";
  /** DSN/API key for error monitoring service */
  dsn?: string;
  /** Environment name (production, staging, development) */
  environment: string;
  /** Sample rate (0.0 to 1.0) - what percentage of errors to capture */
  sampleRate: number;
  /** Performance traces sample rate (0..1) */
  tracesSampleRate: number;
  /** Replay-on-error sample rate (0..1) */
  replaysOnErrorSampleRate: number;
}

/**
 * Load error monitoring configuration from environment variables
 */
function loadErrorMonitoringConfig(): ErrorMonitoringConfig {
  const enabledEnv = import.meta.env.VITE_ERROR_MONITORING_ENABLED;
  const enabled = enabledEnv === "true" || enabledEnv === "1";

  const providerEnv = import.meta.env.VITE_ERROR_MONITORING_PROVIDER;
  const provider = enabled && providerEnv === "sentry" ? "sentry" : "none";

  const dsn = import.meta.env.VITE_ERROR_MONITORING_DSN;

  const environment = import.meta.env.VITE_ENVIRONMENT || 
                     (import.meta.env.PROD ? "production" : "development");

  const sampleRateEnv = import.meta.env.VITE_ERROR_SAMPLE_RATE;
  const sampleRate = sampleRateEnv ? parseFloat(sampleRateEnv) : 1.0;

  const traces = import.meta.env.VITE_ERROR_TRACES_SAMPLE_RATE;
  const tracesSampleRate = traces ? parseFloat(traces) : 0.1;
  const onErr = import.meta.env.VITE_ERROR_REPLAY_ON_ERROR_RATE;
  const replaysOnErrorSampleRate = onErr ? parseFloat(onErr) : 1.0;

  return {
    enabled,
    provider,
    dsn,
    environment,
    sampleRate: Math.max(0, Math.min(1, sampleRate)), // Clamp to 0-1
    tracesSampleRate: Math.max(0, Math.min(1, tracesSampleRate)),
    replaysOnErrorSampleRate: Math.max(0, Math.min(1, replaysOnErrorSampleRate)),
  };
}

/**
 * Global error monitoring configuration
 */
export const ERROR_MONITORING_CONFIG: ErrorMonitoringConfig = loadErrorMonitoringConfig();

/**
 * Check if error monitoring is enabled and configured
 */
export function isErrorMonitoringEnabled(): boolean {
  return ERROR_MONITORING_CONFIG.enabled && 
         ERROR_MONITORING_CONFIG.provider !== "none" &&
         (ERROR_MONITORING_CONFIG.provider === "sentry" ? !!ERROR_MONITORING_CONFIG.dsn : true);
}
